// Per-currency, per-kind cost multipliers applied to the USD baseRate when a
// project is in a non-USD currency AND no explicit Cost Library override
// exists for that item.
//
// Why per-kind? A flat per-currency multiplier doesn't capture that ZA labour
// is much cheaper than ZA materials (most of which are imported and priced
// near-parity with US$). Same applies inversely in NZ/AU where local labour
// is comparable to US labour but materials carry an import premium.
//
// Each kind:
//   - 'labour'   — pure trade-day rates (Demolition, Plumbing, Electrical, etc)
//   - 'material' — pure stuff cost (Sanitaryware, Appliances, Wardrobe, etc)
//   - 'mixed'    — combined labour + materials (Tiling, Paint, Cabinetry, etc)
export const REGION_KIND_MULT = {
  USD: { labour: 1.00, material: 1.00, mixed: 1.00 },
  CAD: { labour: 1.30, material: 1.40, mixed: 1.35 },
  GBP: { labour: 0.65, material: 0.95, mixed: 0.80 },
  EUR: { labour: 0.85, material: 0.95, mixed: 0.90 },
  AUD: { labour: 1.45, material: 1.55, mixed: 1.50 },
  NZD: { labour: 1.55, material: 1.80, mixed: 1.65 },
  ZAR: { labour: 4.00, material: 6.00, mixed: 5.00 },
};

// Backwards-compatible flat multiplier — used by the Cost Library page banner
// and any caller that doesn't know the item's kind. Intentionally aligned with
// the 'mixed' value of each region so it remains a sensible fallback.
export const CURRENCY_MULT = Object.fromEntries(
  Object.entries(REGION_KIND_MULT).map(([k, v]) => [k, v.mixed])
);

export function currencyMultiplier(currency) {
  return CURRENCY_MULT[currency] ?? 1.0;
}

// Look up the per-kind multiplier for a given currency. Defaults to the mixed
// value if the kind is unknown or missing.
export function kindMultiplier(currency, kind) {
  const region = REGION_KIND_MULT[currency] || REGION_KIND_MULT.USD;
  if (kind && region[kind] !== undefined) return region[kind];
  return region.mixed;
}

/**
 * Resolve an effective rate for an item:
 *  - if the user has explicitly set an override, use that verbatim
 *  - otherwise scale the USD base rate by the project's per-kind multiplier
 *    (or the mixed multiplier if `kind` is omitted)
 */
export function effectiveRate(baseRate, override, currency, kind) {
  const o = Number(override);
  if (Number.isFinite(o) && o > 0) return o;
  return Math.round((Number(baseRate) || 0) * kindMultiplier(currency, kind));
}
